import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaPlus, FaEdit, FaTrash, FaSave, FaTimes, FaImage, FaSync, FaExternalLinkAlt, FaArrowUp, FaArrowDown } from 'react-icons/fa';
import { db } from '../../firebase';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, getDoc } from 'firebase/firestore';

const ProjectManager = () => {
  const [projects, setProjects] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [currentProject, setCurrentProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [commonTags, setCommonTags] = useState([]);
  const [filterCategories, setFilterCategories] = useState([]);

  const emptyProject = {
    title: '',
    description: '',
    image: '',
    tags: [],
    category: 'fullstack',
    github: '',
    live: '',
    featured: false,
    showOnMainSite: true, // New field to control visibility
    order: 0, // Display order (lower number = appears first)
  };

  useEffect(() => {
    fetchProjects();
    fetchTags();

    // Listen for tags updates from TagManager
    const handleTagsUpdate = (event) => {
      if (event.detail) {
        setCommonTags(event.detail.commonTags || []);
        setFilterCategories(event.detail.filterCategories || []);
        console.log('✅ Tags refreshed from TagManager update');
      }
    };

    window.addEventListener('tagsUpdated', handleTagsUpdate);

    // Cleanup listener on unmount
    return () => {
      window.removeEventListener('tagsUpdated', handleTagsUpdate);
    };
  }, []);

  const fetchTags = async () => {
    try {
      const tagsDoc = await getDoc(doc(db, 'settings', 'tags'));
      if (tagsDoc.exists()) {
        const data = tagsDoc.data();
        setCommonTags(data.commonTags || []);
        setFilterCategories(data.filterCategories || []);
        console.log('✅ Tags loaded from Firestore');
      } else {
        console.log('⚠️ Tags document not found, using defaults');
        setCommonTags(['React', 'Node.js', 'Python', 'TensorFlow', 'MongoDB', 'AWS', 'Docker', 'TypeScript']);
        setFilterCategories([
          { id: 'genai', label: 'Gen AI' },
          { id: 'ai', label: 'AI & Machine Learning' },
          { id: 'fullstack', label: 'Full Stack' },
          { id: 'devops', label: 'DevOps & Cloud' }
        ]);
      }
    } catch (error) {
      if (error.code === 'unavailable') {
        console.warn('⚠️ Firestore offline - using default tags');
      } else {
        console.error('❌ Error fetching tags:', error);
      }
      // Always use fallback defaults if fetch fails
      setCommonTags(['React', 'Node.js', 'Python', 'TensorFlow', 'MongoDB', 'AWS', 'Docker', 'TypeScript']);
      setFilterCategories([
        { id: 'genai', label: 'Gen AI' },
        { id: 'ai', label: 'AI & Machine Learning' },
        { id: 'fullstack', label: 'Full Stack' },
        { id: 'devops', label: 'DevOps & Cloud' }
      ]);
    }
  };

  const loadDefaultProjects = async () => {
    try {
      console.log('📦 Loading default projects...');
      setLoading(true);
      
      // Import default projects
      const { projects: defaultProjects } = await import('../../Constants/projectsData');
      console.log(`📋 Found ${defaultProjects.length} default projects to import`);
      
      let successCount = 0;
      let errorCount = 0;
      
      // Add each default project to Firestore
      for (let i = 0; i < defaultProjects.length; i++) {
        const project = defaultProjects[i];
        try {
          const { id, ...projectData } = project; // Remove the id field
          
          const docRef = await addDoc(collection(db, 'projects'), {
            ...projectData,
            showOnMainSite: project.featured !== false,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          });
          
          console.log(`✅ Project ${i + 1}/${defaultProjects.length}: ${project.title} (ID: ${docRef.id})`);
          successCount++;
        } catch (err) {
          console.error(`❌ Failed to add project ${i + 1}:`, project.title, err);
          errorCount++;
        }
      }
      
      console.log(`✨ Import complete: ${successCount} succeeded, ${errorCount} failed`);
      alert(`Successfully imported ${successCount} default projects!`);
      
      // Refresh the projects list
      await fetchProjects();
    } catch (error) {
      console.error('❌ Error loading default projects:', error);
      alert(`Error loading defaults: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      setLoading(true);
      console.log('📥 Fetching projects from Firestore...');
      
      const projectsSnapshot = await getDocs(collection(db, 'projects'));
      console.log(`✅ Found ${projectsSnapshot.docs.length} projects in database`);
      
      const projectsList = projectsSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          tags: Array.isArray(data.tags) ? data.tags : [],
          featured: Boolean(data.featured),
          showOnMainSite: data.showOnMainSite !== false,
          category: data.category || 'fullstack',
          order: typeof data.order === 'number' ? data.order : 999 // Default to end if no order
        };
      });
      
      // Sort by order field (ascending)
      projectsList.sort((a, b) => a.order - b.order);
      
      console.log('📋 Projects loaded and sorted:', projectsList);
      console.log(`✅ ${projectsList.length} projects ready for display with order controls`);
      setProjects(projectsList);
      
      // Auto-normalize order on load to ensure sequential values
      const needsNormalization = projectsList.some((p, index) => p.order !== index);
      if (needsNormalization && projectsList.length > 0) {
        console.log('⚙️ Order values need normalization, fixing...');
        setTimeout(async () => {
          try {
            const batch = [];
            projectsList.forEach((project, index) => {
              if (project.order !== index) {
                batch.push(updateDoc(doc(db, 'projects', project.id), { order: index }));
              }
            });
            if (batch.length > 0) {
              await Promise.all(batch);
              console.log(`✅ Normalized ${batch.length} projects to sequential order`);
            }
          } catch (err) {
            console.warn('⚠️ Could not normalize order:', err);
          }
        }, 1000);
      }
      
      // If no projects, show helpful message
      if (projectsList.length === 0) {
        console.log('⚠️ No projects found. Click "Load Defaults" to import sample projects.');
        
        // Show alert once per session
        if (!sessionStorage.getItem('showedEmptyProjectsAlert')) {
          sessionStorage.setItem('showedEmptyProjectsAlert', 'true');
          setTimeout(() => {
            const shouldLoad = confirm(
              '💡 Your database is empty!\n\n' +
              'Would you like to load 8 sample AI/ML projects now?\n\n' +
              '• Click OK to auto-load sample projects\n' +
              '• Click Cancel to add projects manually'
            );
            if (shouldLoad) {
              loadDefaultProjects();
            }
          }, 500);
        }
      }
    } catch (error) {
      console.error('❌ Error fetching projects:', error);
      if (error.code === 'unavailable') {
        alert('⚠️ Cannot connect to Firebase.\n\nPlease check:\n1. Internet connection\n2. Firestore security rules are published\n3. Firebase project is active');
      } else {
        alert(`Error loading projects: ${error.message}\n\nCheck browser console for details.`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      console.log('💾 Saving project...', currentProject.title);
      console.log('📸 Image URL:', currentProject.image || 'NO IMAGE PROVIDED');
      
      // Warn if no image
      if (!currentProject.image || currentProject.image.trim() === '') {
        const proceed = confirm('⚠️ No image URL provided!\n\nThe project will be saved without an image.\n\nContinue anyway?');
        if (!proceed) return;
      }
      
      if (isEditing && currentProject.id) {
        // Update existing project
        console.log('✏️ Updating existing project:', currentProject.id);
        const projectRef = doc(db, 'projects', currentProject.id);
        await updateDoc(projectRef, {
          ...currentProject,
          tags: currentProject.tags.filter(tag => tag.trim() !== ''),
          image: currentProject.image?.trim() || '', // Ensure clean image URL
          updatedAt: new Date().toISOString()
        });
        console.log('✅ Project updated successfully!');
      } else {
        // Add new project
        console.log('➕ Adding new project...');
        const docRef = await addDoc(collection(db, 'projects'), {
          ...currentProject,
          tags: currentProject.tags.filter(tag => tag.trim() !== ''),
          image: currentProject.image?.trim() || '', // Ensure clean image URL
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        });
        console.log('✅ Project added successfully! ID:', docRef.id);
      }
      
      alert(`Project "${currentProject.title}" saved successfully! ✅`);
      await fetchProjects();
      setShowForm(false);
      setCurrentProject(null);
      setIsEditing(false);
    } catch (error) {
      console.error('❌ Error saving project:', error);
      alert(`Error saving project: ${error.message}\nCheck console for details.`);
    }
  };

  const handleDelete = async (projectId) => {
    if (!confirm('Are you sure you want to delete this project?')) return;
    
    try {
      await deleteDoc(doc(db, 'projects', projectId));
      fetchProjects();
    } catch (error) {
      console.error('Error deleting project:', error);
      alert('Error deleting project. Please try again.');
    }
  };

  const normalizeOrder = async () => {
    // Assign sequential order values (0, 1, 2, 3...) based on current sort
    try {
      const batch = [];
      projects.forEach((project, index) => {
        if (project.order !== index) {
          batch.push(updateDoc(doc(db, 'projects', project.id), { order: index }));
        }
      });
      if (batch.length > 0) {
        await Promise.all(batch);
        console.log(`✅ Normalized order for ${batch.length} projects`);
      }
    } catch (error) {
      console.error('Error normalizing order:', error);
    }
  };

  const moveProjectUp = async (projectId) => {
    const currentIndex = projects.findIndex(p => p.id === projectId);
    if (currentIndex <= 0) return; // Already at top

    const currentProject = projects[currentIndex];
    const previousProject = projects[currentIndex - 1];

    try {
      // Swap order values
      const tempOrder = currentProject.order;
      await updateDoc(doc(db, 'projects', currentProject.id), { order: previousProject.order });
      await updateDoc(doc(db, 'projects', previousProject.id), { order: tempOrder });
      
      await fetchProjects(); // Refresh list
      await normalizeOrder(); // Ensure sequential order
    } catch (error) {
      console.error('Error moving project up:', error);
      alert('Error reordering project. Please try again.');
    }
  };

  const moveProjectDown = async (projectId) => {
    const currentIndex = projects.findIndex(p => p.id === projectId);
    if (currentIndex >= projects.length - 1) return; // Already at bottom

    const currentProject = projects[currentIndex];
    const nextProject = projects[currentIndex + 1];

    try {
      // Swap order values
      const tempOrder = currentProject.order;
      await updateDoc(doc(db, 'projects', currentProject.id), { order: nextProject.order });
      await updateDoc(doc(db, 'projects', nextProject.id), { order: tempOrder });
      
      await fetchProjects(); // Refresh list
      await normalizeOrder(); // Ensure sequential order
    } catch (error) {
      console.error('Error moving project down:', error);
      alert('Error reordering project. Please try again.');
    }
  };

  const handleEdit = (project) => {
    setCurrentProject(project);
    setIsEditing(true);
    setShowForm(true);
  };

  const handleAddNew = () => {
    setCurrentProject(emptyProject);
    setIsEditing(false);
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setCurrentProject(null);
    setIsEditing(false);
  };

  const handleTagInput = (value) => {
    const tagsArray = value.split(',').map(tag => tag.trim()).filter(tag => tag !== '');
    setCurrentProject({ ...currentProject, tags: tagsArray });
  };

  const addTag = (tag) => {
    if (tag && !currentProject.tags.includes(tag.trim())) {
      setCurrentProject({ 
        ...currentProject, 
        tags: [...currentProject.tags, tag.trim()] 
      });
    }
  };

  const removeTag = (tagToRemove) => {
    setCurrentProject({ 
      ...currentProject, 
      tags: currentProject.tags.filter(tag => tag !== tagToRemove) 
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-3xl font-bold text-white">Project Management</h2>
          <p className="text-gray-400 text-sm mt-1">
            Total: {projects.length} projects | 
            Featured: {projects.filter(p => p.featured).length} | 
            Main Site: {projects.filter(p => p.showOnMainSite !== false).length}
          </p>
        </div>
        {!showForm && (
          <div className="flex gap-3">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={async () => {
                await fetchTags();
                alert('Tags refreshed! ✅');
              }}
              className="flex items-center space-x-2 px-4 py-3 bg-green-600 text-white rounded-lg font-medium shadow-lg hover:bg-green-700"
              title="Refresh tags and categories from TagManager"
            >
              <FaSync />
              <span>Refresh Tags</span>
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={async () => {
                if (confirm('Load default projects from code? This will add them to your database.')) {
                  await loadDefaultProjects();
                  await fetchProjects();
                }
              }}
              className="flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-medium shadow-lg hover:bg-blue-700"
            >
              <span>📥 Load Defaults</span>
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleAddNew}
              className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-medium shadow-lg"
            >
              <FaPlus />
              <span>Add New Project</span>
            </motion.button>
          </div>
        )}
      </div>

      {/* Form */}
      <AnimatePresence>
        {showForm && currentProject && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-[#1d1836] rounded-2xl p-8 border border-purple-500/20"
          >
            <h3 className="text-2xl font-bold text-white mb-6">
              {isEditing ? 'Edit Project' : 'Add New Project'}
            </h3>
            <form onSubmit={handleSave} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Project Title
                  </label>
                  <input
                    type="text"
                    value={currentProject.title}
                    onChange={(e) => setCurrentProject({ ...currentProject, title: e.target.value })}
                    className="w-full px-4 py-3 bg-[#100d25] border border-purple-500/30 rounded-lg text-white focus:outline-none focus:border-purple-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Tags (comma-separated or click to add)
                  </label>
                  
                  {/* Display current tags as chips */}
                  <div className="flex flex-wrap gap-2 mb-3">
                    {currentProject.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-purple-500/20 text-purple-300 rounded-full text-sm flex items-center space-x-2"
                      >
                        <span>{tag}</span>
                        <button
                          type="button"
                          onClick={() => removeTag(tag)}
                          className="text-red-400 hover:text-red-300"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                  
                  {/* Input for adding tags */}
                  <input
                    type="text"
                    value={currentProject.tags.join(', ')}
                    onChange={(e) => handleTagInput(e.target.value)}
                    className="w-full px-4 py-3 bg-[#100d25] border border-purple-500/30 rounded-lg text-white focus:outline-none focus:border-purple-500"
                    placeholder="React, Node.js, MongoDB (separate with commas)"
                  />
                  
                  {/* Quick add common tags */}
                  <div className="mt-2">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs text-gray-400">Quick add:</p>
                      {commonTags.length === 0 && (
                        <p className="text-xs text-yellow-400">No tags found. Add them in Tags tab!</p>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {commonTags.map(tag => (
                        <button
                          key={tag}
                          type="button"
                          onClick={() => addTag(tag)}
                          className="px-2 py-1 bg-purple-500/10 text-purple-300 text-xs rounded hover:bg-purple-500/20 transition"
                        >
                          + {tag}
                        </button>
                      ))}
                      {commonTags.length === 0 && (
                        <p className="text-xs text-gray-500 italic">
                          Go to Tags tab to add custom tags, then click "Refresh Tags" button above.
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Category
                  </label>
                  <select
                    value={currentProject.category}
                    onChange={(e) => setCurrentProject({ ...currentProject, category: e.target.value })}
                    className="w-full px-4 py-3 bg-[#100d25] border border-purple-500/30 rounded-lg text-white focus:outline-none focus:border-purple-500"
                  >
                    {filterCategories.filter(cat => cat.id !== 'all').map(category => (
                      <option key={category.id} value={category.id}>{category.label}</option>
                    ))}
                    {filterCategories.filter(cat => cat.id !== 'all').length === 0 && (
                      <option value="">No categories found - Add in Tags tab</option>
                    )}
                  </select>
                  {filterCategories.filter(cat => cat.id !== 'all').length === 0 && (
                    <p className="text-xs text-yellow-400 mt-1">
                      💡 Add categories in Tags tab, then click "Refresh Tags" button above.
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  value={currentProject.description}
                  onChange={(e) => setCurrentProject({ ...currentProject, description: e.target.value })}
                  className="w-full px-4 py-3 bg-[#100d25] border border-purple-500/30 rounded-lg text-white focus:outline-none focus:border-purple-500 h-32"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Project Image URL
                  </label>
                  
                  {/* Helpful Instructions */}
                  <div className="mb-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                    <p className="text-xs text-blue-300 mb-2 font-semibold">📸 How to add an image:</p>
                    <ol className="text-xs text-gray-300 space-y-1 list-decimal list-inside">
                      <li>Upload your image to <a href="https://imgur.com" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 underline">Imgur</a> (free, no signup)</li>
                      <li>Right-click the uploaded image → "Copy image address"</li>
                      <li>Paste the URL below (should end with .jpg, .png, etc.)</li>
                    </ol>
                    <div className="mt-2 pt-2 border-t border-blue-500/30">
                      <p className="text-xs text-gray-400">
                        <strong>Other options:</strong> <a href="https://cloudinary.com" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">Cloudinary</a>, <a href="https://imgbb.com" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">ImgBB</a>, or GitHub
                      </p>
                    </div>
                  </div>

                  {/* URL Input */}
                  <div className="relative">
                    <FaImage className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={currentProject.image}
                      onChange={(e) => setCurrentProject({ ...currentProject, image: e.target.value })}
                      className={`w-full pl-10 pr-4 py-3 bg-[#100d25] border rounded-lg text-white focus:outline-none transition ${
                        !currentProject.image 
                          ? 'border-yellow-500/50 focus:border-yellow-500' 
                          : 'border-purple-500/30 focus:border-purple-500'
                      }`}
                      placeholder="https://i.imgur.com/example.jpg"
                    />
                    {!currentProject.image && (
                      <p className="text-xs text-yellow-400 mt-1 flex items-center gap-1">
                        <span>⚠️</span>
                        <span>Image URL required - Project will show placeholder if empty</span>
                      </p>
                    )}
                  </div>

                  {/* Quick Link Buttons */}
                  <div className="mt-2 flex flex-wrap gap-2">
                    <a
                      href="https://imgur.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center space-x-1 px-3 py-1 bg-green-500/20 text-green-300 text-xs rounded hover:bg-green-500/30 transition"
                    >
                      <FaExternalLinkAlt className="text-[10px]" />
                      <span>Open Imgur</span>
                    </a>
                    <a
                      href="https://imgbb.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center space-x-1 px-3 py-1 bg-green-500/20 text-green-300 text-xs rounded hover:bg-green-500/30 transition"
                    >
                      <FaExternalLinkAlt className="text-[10px]" />
                      <span>Open ImgBB</span>
                    </a>
                  </div>

                  {/* Image Preview */}
                  {currentProject.image ? (
                    <div className="mt-3">
                      <p className="text-xs text-gray-400 mb-2">Preview:</p>
                      <div className="relative">
                        <img
                          src={currentProject.image}
                          alt="Preview"
                          className="w-full h-32 object-cover rounded-lg border border-green-500/30"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextElementSibling.style.display = 'flex';
                          }}
                          onLoad={(e) => {
                            e.target.nextElementSibling.style.display = 'none';
                          }}
                        />
                        <div 
                          className="w-full h-32 bg-red-500/10 border border-red-500/30 rounded-lg items-center justify-center text-center p-4" 
                          style={{ display: 'none' }}
                        >
                          <div className="text-red-400 text-sm">
                            <div className="text-2xl mb-2">❌</div>
                            <div>Failed to load image</div>
                            <div className="text-xs mt-1 opacity-70">Check if URL is valid</div>
                          </div>
                        </div>
                      </div>
                      <p className="text-xs text-green-400 mt-1">✅ Image URL provided</p>
                    </div>
                  ) : (
                    <div className="mt-3 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg text-center">
                      <div className="text-4xl mb-2">📷</div>
                      <p className="text-sm text-yellow-300">No image URL - Upload to Imgur first!</p>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    GitHub URL
                  </label>
                  <input
                    type="url"
                    value={currentProject.github}
                    onChange={(e) => setCurrentProject({ ...currentProject, github: e.target.value })}
                    className="w-full px-4 py-3 bg-[#100d25] border border-purple-500/30 rounded-lg text-white focus:outline-none focus:border-purple-500"
                    placeholder="https://github.com/username/repo"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Live Demo URL
                  </label>
                  <input
                    type="url"
                    value={currentProject.live}
                    onChange={(e) => setCurrentProject({ ...currentProject, live: e.target.value })}
                    className="w-full px-4 py-3 bg-[#100d25] border border-purple-500/30 rounded-lg text-white focus:outline-none focus:border-purple-500"
                    placeholder="https://demo.example.com"
                  />
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center space-x-4">
                  <input
                    type="checkbox"
                    checked={currentProject.featured}
                    onChange={(e) => setCurrentProject({ ...currentProject, featured: e.target.checked })}
                    className="w-5 h-5 text-purple-600 bg-[#100d25] border-purple-500/30 rounded focus:ring-purple-500"
                  />
                  <label className="text-sm font-medium text-gray-300">
                    Featured Project (Highlighted in listings)
                  </label>
                </div>

                <div className="flex items-center space-x-4">
                  <input
                    type="checkbox"
                    checked={currentProject.showOnMainSite !== false}
                    onChange={(e) => setCurrentProject({ ...currentProject, showOnMainSite: e.target.checked })}
                    className="w-5 h-5 text-blue-600 bg-[#100d25] border-blue-500/30 rounded focus:ring-blue-500"
                  />
                  <label className="text-sm font-medium text-gray-300">
                    Show on Main Site (Homepage) - Uncheck to show only on Projects page
                  </label>
                </div>
              </div>

              <div className="flex space-x-4">
                <button
                  type="submit"
                  className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-green-600 to-green-500 text-white rounded-lg font-medium hover:from-green-700 hover:to-green-600 transition"
                >
                  <FaSave />
                  <span>Save Project</span>
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="flex items-center space-x-2 px-6 py-3 bg-gray-600 text-white rounded-lg font-medium hover:bg-gray-700 transition"
                >
                  <FaTimes />
                  <span>Cancel</span>
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Projects List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {projects.map((project, projectIndex) => (
          <motion.div
            key={project.id}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[#1d1836] rounded-xl p-6 border border-purple-500/20 hover:border-purple-500/40 transition"
          >
            <div className="mb-4 h-40 bg-[#100d25] rounded-lg overflow-hidden">
              {project.image && typeof project.image === 'string' && project.image.trim() !== '' ? (
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="200"%3E%3Crect fill="%23334155" width="400" height="200"/%3E%3Ctext fill="%239CA3AF" x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="system-ui" font-size="18px"%3EImage Not Found%3C/text%3E%3C/svg%3E';
                  }}
                />
              ) : (
                <div className="w-full h-full relative flex items-center justify-center bg-gradient-to-br from-slate-700 to-slate-800">
                  <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,.1) 10px, rgba(255,255,255,.1) 20px)' }}></div>
                  <div className="text-center text-gray-500 z-10">
                    <div className="text-4xl mb-1 opacity-40">📷</div>
                    <div className="text-xs">No Image</div>
                  </div>
                </div>
              )}
            </div>
            <div className="flex items-start justify-between mb-2">
              <h3 className="text-xl font-bold text-white flex-1">{project.title}</h3>
              <div className="flex gap-1">
                {project.featured && (
                  <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs rounded">
                    ⭐ Featured
                  </span>
                )}
                {project.showOnMainSite !== false && (
                  <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded">
                    🏠 Main
                  </span>
                )}
              </div>
            </div>
            <p className="text-gray-400 text-sm mb-4 line-clamp-2">
              {project.description}
            </p>
            <div className="flex flex-wrap gap-2 mb-4">
              {project.tags?.slice(0, 3).map((tag, index) => (
                <span
                  key={index}
                  className="px-2 py-1 bg-purple-500/10 text-purple-400 text-xs rounded"
                >
                  {tag}
                </span>
              ))}
              {project.tags?.length > 3 && (
                <span className="px-2 py-1 bg-purple-500/10 text-purple-400 text-xs rounded">
                  +{project.tags.length - 3}
                </span>
              )}
            </div>
            
            {/* Order controls */}
            <div className="flex items-center justify-between mb-3 pb-3 border-b border-purple-500/20">
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-400">Position:</span>
                <span className="text-sm text-purple-400 font-bold">#{projectIndex + 1} of {projects.length}</span>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => moveProjectUp(project.id)}
                  disabled={projectIndex === 0}
                  className="flex items-center justify-center w-10 h-10 bg-purple-500/20 text-purple-300 rounded-lg hover:bg-purple-500/30 transition disabled:opacity-25 disabled:cursor-not-allowed border border-purple-500/30"
                  title="Move up"
                >
                  <FaArrowUp className="text-lg" />
                </button>
                <button
                  onClick={() => moveProjectDown(project.id)}
                  disabled={projectIndex === projects.length - 1}
                  className="flex items-center justify-center w-10 h-10 bg-purple-500/20 text-purple-300 rounded-lg hover:bg-purple-500/30 transition disabled:opacity-25 disabled:cursor-not-allowed border border-purple-500/30"
                  title="Move down"
                >
                  <FaArrowDown className="text-lg" />
                </button>
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => handleEdit(project)}
                className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-blue-500/10 text-blue-400 rounded-lg hover:bg-blue-500/20 transition"
              >
                <FaEdit />
                <span>Edit</span>
              </button>
              <button
                onClick={() => handleDelete(project.id)}
                className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 transition"
              >
                <FaTrash />
                <span>Delete</span>
              </button>
            </div>
          </motion.div>
        ))}
      </div>

      {projects.length === 0 && !showForm && (
        <div className="text-center py-16">
          <p className="text-gray-400 text-lg">No projects yet. Add your first project!</p>
        </div>
      )}
    </div>
  );
};

export default ProjectManager;
